import {
  breakpoints,
  Col,
  DropdownMenu,
  Icon,
  IconButton,
  Tappable,
  Text,
  Tooltip,
  useTheme,
} from "@rally/ui-kit";
import { say } from "../../localization/say.ts";
import { focusMetadata } from "../attachment/file-focus.ts";
import type { FocusChrome } from "./panel-focus.tsx";
import type { PanelAction } from "./panels.tsx";

export const FOCUS_BANNER_HEIGHT = 56;

export function FocusBanner({
  title,
  chrome,
  actions,
  onBack,
}: {
  title: string;
  chrome: FocusChrome | null;
  actions: readonly PanelAction[];
  onBack: () => void;
}) {
  const theme = useTheme();
  const phone = theme.isAtMost(breakpoints.phone);
  const medium = theme.isAtMost(breakpoints.tablet);
  const download = actions.find((action) => action.key === "download");
  const choice = chrome?.choice;
  const overflow = chrome
    ? [
        ...(chrome.source ? [{ label: chrome.source.label, onPress: chrome.source.open }] : []),
        { label: say("files.size")(chrome.size), disabled: true },
        ...chrome.facts.map((fact) => ({ label: fact.label, disabled: true })),
        ...(choice
          ? (["preview", "source"] as const).map((view) => ({
              label: say(view === "preview" ? "files.preview" : "files.source"),
              onPress: () => choice.change(view),
            }))
          : []),
        ...(download
          ? [{ label: download.label, disabled: download.disabled, onPress: download.onPress }]
          : []),
      ]
    : [];
  return (
    <Col
      testID="file-focus-banner"
      style={{
        height: FOCUS_BANNER_HEIGHT,
        flexDirection: "row",
        alignItems: "center",
        gap: theme.space[2],
        paddingHorizontal: theme.space[2],
        borderBottomWidth: 1,
        borderBottomColor: theme.color["border.default"],
      }}
    >
      <Tooltip
        content={say("files.backToConversation")}
        below
        onPress={onBack}
        accessibilityLabel={say("files.backToConversation")}
        testID="file-focus-back"
      >
        <Col
          style={{
            width: 40,
            height: 40,
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Icon name="arrow-left" size={18} color="text.primary" />
        </Col>
      </Tooltip>
      <Text
        typeName="bodySemibold"
        color="text.primary"
        numberOfLines={1}
        style={{ minWidth: 0, flexShrink: 1 }}
      >
        {title}
      </Text>
      {choice && !phone ? (
        <Col style={{ flexDirection: "row", gap: theme.space[1], flexShrink: 0 }}>
          {(["preview", "source"] as const).map((view) => (
            <Tappable
              key={view}
              accessibilityRole="button"
              accessibilityState={{ selected: choice.view === view }}
              testID={`file-focus-${view}`}
              onPress={() => choice.change(view)}
              style={{ paddingHorizontal: theme.space[2], paddingVertical: theme.space[1] }}
            >
              <Text typeName="caption" color={choice.view === view ? "accent" : "text.tertiary"}>
                {say(view === "preview" ? "files.preview" : "files.source")}
              </Text>
            </Tappable>
          ))}
        </Col>
      ) : null}
      <Col style={{ flex: 1 }} />
      {chrome?.source && !phone ? (
        <Tappable accessibilityRole="link" testID="file-focus-conversation" onPress={chrome.source.open}>
          <Text typeName="caption" color="accent" numberOfLines={1}>
            {chrome.source.label}
          </Text>
        </Tappable>
      ) : null}
      {chrome ? (
        <Text typeName="caption" color="text.tertiary" numberOfLines={1} testID="file-focus-metadata">
          {focusMetadata(
            chrome.size,
            chrome.facts.map((fact) => fact.label),
            medium,
          )}
        </Text>
      ) : null}
      {!medium && download ? (
        <IconButton
          icon="download"
          ariaLabel={download.label}
          testID="file-focus-download"
          disabled={download.disabled}
          onPress={download.onPress}
        />
      ) : null}
      {medium && overflow.length > 0 ? (
        <DropdownMenu
          items={overflow}
          align="right"
          width={240}
          trigger={
            <Col testID="file-focus-overflow" style={{ pointerEvents: "none" }}>
              <IconButton icon="more-horizontal" ariaLabel={say("files.moreDetails")} />
            </Col>
          }
        />
      ) : null}
    </Col>
  );
}
